import {
  IWebflowEnterprise,
  IWebflowPartner,
  IWebflowPartnerSignUpTypes
} from "@wingspanhq/cms/dist/lib/interfaces";
import {
  useModalContext,
  WSButton,
  WSFlexBox,
  WSLoader,
  WSGrid,
  WSModal,
  WSScreen,
  WSText,
  useIsMobile,
  WSElement
} from "@wingspanhq/fe-component-library";
import { SessionType } from "@wingspanhq/users/dist/lib/interfaces/session";
import queryString from "query-string";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SignUpForm } from "../../Auth/components/SignUpForm/SignUpForm";
import { BrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import { OnboardingLayout } from "../../components/OnboardingLayout/OnboardingLayout";
import { useLocalStorageState } from "../../hooks/useLocalStorage";
import {
  FeatureInfoProps,
  FeatureInfo
} from "../../MemberOnboarding/components/FeatureInfo/FeatureInfo";
import {
  useCMSWebflowEnterprise,
  useCMSWebflowPartner
} from "../../query/cms/queries";
import { useUserId } from "../../query/hooks/helpers";
import {
  getCurrentEntepriseId,
  getCurrentPartnerId
} from "../../query/platform/selectors";
import { useSession } from "../../query/session";
import { useCreateSession } from "../../query/users/mutations";
import { useUserTag } from "../../query/users/queries";
import { useSetWSStore, useWSStore } from "../../store";
import { openInNewTab } from "../../utils/openInNewTab";
import { WSVirtualAssistant } from "../components/WSVirtualAssistant/WSVirtualAssistant";

import styles from "./MemberSignUp.module.scss";

export interface SignUpWelcomeProps extends FeatureInfoProps {
  name?: string;
  isEnterprise?: boolean;
  showHeader?: boolean;
  showContent?: boolean;
}

export const SignUpWelcome: React.FC<SignUpWelcomeProps> = ({
  title,
  description,
  featureItems,
  name,
  isEnterprise,
  showHeader = true,
  showContent = true
}) => {
  const store = useWSStore();
  const isMobile = useIsMobile();
  const { openModal } = useModalContext();

  const { is1099SignUp } = store;

  const onClickFAQ = () => {
    openInNewTab("https://wingspan.readme.io/docs/frequently-asked-questions");
  };

  const contactMail = () => {
    window.open("mailto:support@wingspan.app");
  };

  if (is1099SignUp) {
    return (
      <WSElement>
        {showHeader ? (
          <WSText.Heading2 mb="L">
            {`${name} partners with Wingspan to manage and deliver your tax forms`}
          </WSText.Heading2>
        ) : null}
        {showContent ? (
          <WSElement data-testid="1099-sign-up-content">
            <WSText.Paragraph mt="M" mb="M">
              <WSText.LabelBold inline mr="XS">
                What is Wingspan?
              </WSText.LabelBold>
              Wingspan is a secure web app which allows you to access your tax
              forms at any time. Once you get started, you'll be prompted to
              verify your identity and optionally enable e-delivery.
            </WSText.Paragraph>

            <WSText.Paragraph mb="M">
              <WSText.LabelBold inline mr="XS">
                Do I need to sign up for Wingspan?
              </WSText.LabelBold>
              If you have already provided your W-9 form to {`${name}`}, you do
              not need to sign up for Wingspan unless you'd like to update your
              address or W-9 information on file.
            </WSText.Paragraph>

            <WSText.Paragraph mb="M">
              <WSText.LabelBold inline mr="XS">
                What is a 1099-NEC tax form?
              </WSText.LabelBold>
              A 1099-NEC form summarizes your earnings as an independent
              contractor. It's provided to you and the IRS, as well as some US
              states, if you earned 600 USD or more in 2022.
            </WSText.Paragraph>

            <WSText.Paragraph mb="M">
              <WSText.LabelBold inline mr="XS">
                Why do I need to verify my information?
              </WSText.LabelBold>
              We want to ensure that information like your name, address, and
              Taxpayer Identification Number are up to date so that your 1099
              tax form is accurate and sent to the right place.
            </WSText.Paragraph>

            <WSText.Paragraph mb="M">
              <WSText.LabelBold inline mr="XS">
                When and where can I expect my 1099 tax form?
              </WSText.LabelBold>
              1099 tax forms are typically finalized in January. If you meet the
              income thresholds above, Wingspan will send you a link to access
              your 1099 tax form (or mail a paper form if you prefer).
            </WSText.Paragraph>

            <WSText.Paragraph mb="M">
              <WSText.LabelBold inline mr="XS">
                Need more help getting setup?
              </WSText.LabelBold>
              Review the{" "}
              <WSButton.Link
                inline
                className={styles.faq}
                onClick={onClickFAQ}
                mr="XS"
              >
                frequently asked questions
              </WSButton.Link>
              here or contact us at
              <WSButton.Link inline onClick={contactMail}>
                support@wingspan.app
              </WSButton.Link>
            </WSText.Paragraph>
          </WSElement>
        ) : null}
      </WSElement>
    );
  }
  return (
    <>
      <WSScreen.Mobile>
        <WSText.Heading2 mb="S">{title}</WSText.Heading2>
        <WSText mb="XS">{description}</WSText>
        <WSButton.Link
          mb="M"
          onClick={() => {
            openModal("SIGN_UP_FEATURES_LIST");
          }}
        >
          Learn more
        </WSButton.Link>

        <WSModal name="SIGN_UP_FEATURES_LIST">
          <FeatureInfo
            pt={isMobile ? "M" : "S"}
            title={title}
            description={description}
            featureItems={featureItems}
          />
        </WSModal>
      </WSScreen.Mobile>
      <WSScreen.TabletAndDesktop>
        <FeatureInfo
          pt={isMobile ? "M" : "S"}
          title={title}
          description={description}
          featureItems={featureItems}
        />
        {!isEnterprise && (
          <WSVirtualAssistant
            mt="M"
            message="Wingspan is entirely tax deductible! And - you guessed it - we'll deduct it for you."
          />
        )}
      </WSScreen.TabletAndDesktop>
    </>
  );
};

export const MemberSignUpV2: React.FC = () => {
  const { openModal } = useModalContext();

  const location = useLocation();
  const store = useWSStore();

  const [_, setSignUpPartner] = useLocalStorageState(
    "wingspan:sign-up-with-partner",
    ""
  );

  const history = useHistory();
  const setStore = useSetWSStore();
  const queries = queryString.parse(window.location.search);

  const email = queries.email as string | undefined;

  const firstName = queries.firstName as string | undefined;
  const companyName = queries.companyName as string | undefined;
  const refereeEmail = queries.refereeEmail as string | undefined;

  const inviteType = queries.inviteType as string | undefined;
  const inviterName = queries.inviterName as string | undefined;
  const inviterCompany = queries.inviterCompany as string | undefined;
  const inviteeEmail = queries.inviteeEmail as string | undefined;

  const [
    isLoadingCustomSignUpExperience,
    setIsLoadingCustomSignUpExperience
  ] = useState(false);

  const partnerId = getCurrentPartnerId(store);
  const partnerQuery = useCMSWebflowPartner(partnerId, {
    enabled: false,
    retry: false
  });
  const partner = partnerQuery.data;

  const enterpriseId = getCurrentEntepriseId(store);
  const enterpriseQuery = useCMSWebflowEnterprise(enterpriseId || "", {
    enabled: false,
    retry: false
  });

  const sessionQuery = useSession({
    enabled: false,
    retry: false
  });

  const [signInAsGuest] = useCreateSession();

  const userTagQuery = useUserTag(enterpriseId || partnerId || "", {
    enabled: false,
    retry: false
  });

  const [
    alreadyCheckedForCustomSignUpExperience,
    setAlreadyCheckedForCustomSignUpExperience
  ] = useState(false);

  const [
    isProbablyCheckingV2Captcha,
    setIsProbablyCheckingV2Captcha
  ] = useState(false);

  const [
    alreadyCheckedUserTagAfterV2Captcha,
    setAlreadyCheckedUserTagAfterV2Captcha
  ] = useState(false);

  const checkGrowthName = async () => {
    // this is painful because we're breaking RQ, but doing it this way just helps maintain order with the network calls
    // if we're here, it's because someone has put in either an enterpriseId or a partnerId and we've never run this before
    setIsLoadingCustomSignUpExperience(true);
    setAlreadyCheckedForCustomSignUpExperience(true);
    let isDataInCMS = false;
    if (!!enterpriseId) {
      // check if there's a matching CMS enterprise entry
      const result = await enterpriseQuery.refetch();
      if (result) {
        // if so, we're done. use that info!
        setIsLoadingCustomSignUpExperience(false);
        isDataInCMS = true;
      }
    } else {
      //  check if there's a matching CMS partner entry
      const result = await partnerQuery.refetch();
      if (result) {
        // if so, we're done. use that info!
        setIsLoadingCustomSignUpExperience(false);
      }
      isDataInCMS = true;
    }
    if (!isDataInCMS) {
      // if none of the above, we're gonna create a guest session and check the user tag
      const result = await signInAsGuest();
      if (result) {
        // once the guest session returns, let's fetch the user tag
        await userTagQuery.refetch();
        setIsLoadingCustomSignUpExperience(false);
      } else {
        // we probably had session creation error
        setIsProbablyCheckingV2Captcha(true);
      }
    } else {
      setIsLoadingCustomSignUpExperience(false);
      console.error(
        `No CMS entry or userTag matches ${enterpriseId || partnerId}`
      );
    }
  };

  const checkUserTagIfWeHadToDoV2Captcha = async () => {
    // so we can get here only if we got interrupted by the V2 captcha and need to run the userTagQuery
    setAlreadyCheckedUserTagAfterV2Captcha(true);
    await userTagQuery.refetch();
    setIsLoadingCustomSignUpExperience(false);
  };

  useEffect(() => {
    if (
      sessionQuery.data?.sessionType === SessionType.guest &&
      isProbablyCheckingV2Captcha &&
      !alreadyCheckedUserTagAfterV2Captcha
    ) {
      checkUserTagIfWeHadToDoV2Captcha();
    }
  }, [
    sessionQuery,
    isProbablyCheckingV2Captcha,
    alreadyCheckedUserTagAfterV2Captcha
  ]);

  useEffect(() => {
    if (
      (!!enterpriseId || !!partnerId) &&
      !partnerQuery.isFetched &&
      !enterpriseQuery.isFetched &&
      !isLoadingCustomSignUpExperience &&
      !alreadyCheckedForCustomSignUpExperience
    ) {
      checkGrowthName();
    }
  }, [
    enterpriseId,
    partnerId,
    partnerQuery,
    enterpriseQuery,
    isLoadingCustomSignUpExperience,
    alreadyCheckedForCustomSignUpExperience
  ]);

  const enterpriseForUserTag = userTagQuery.data
    ? {
        is1099FilingOnly: false,
        isGiftCardPayoutsEnabled: false,
        logo: {
          src: userTagQuery.data.member?.profile?.logoUrl
        },
        name: "Wingspan",
        signUpDescription: `Create your free account now, setup your direct deposit information, and get paid!`,
        signUpDescriptionItems: `Tax Withholding: Automatically set aside for taxes from your income. Few basic questions and we can tell you how much federal and state tax you’ll owe.
Federal Tax Payments: Make a quarterly or annual payment without leaving Wingspan.        
Get Insured: Access health, dental and vision insurance at discounted rates.        
Big-Company Benefits: 24/7 Telemedicine, Health Advocate and Tax Hotline.        
Income & Expense Tracking: Link your bank account and identify deductible expenses. Export tax-ready financials.        
Fast payments: Receive payments, send invoices, accept digital payments with ACH and cards.`,
        signUpFormHeader: "Sign up to get paid",
        signUpHeader: `${userTagQuery.data.member?.profile?.company?.name} wants to pay you through Wingspan.`,
        slug: "wingspan"
      }
    : undefined;

  const enterprise = enterpriseQuery.data || enterpriseForUserTag;

  useEffect(() => {
    if (partner) {
      setSignUpPartner(partner.slug);
      setStore({ signUpType: partner.signUpType });
      setStore({ discountCode: partner.stripeDiscountCode });
    }
  }, [partner]);

  useEffect(() => {
    if (firstName) {
      setStore({ signUpType: IWebflowPartnerSignUpTypes.CollaboratorPayments });
    }
  }, [firstName]);

  useEffect(() => {
    if (inviteType === "teams") {
      setStore({ signUpType: IWebflowPartnerSignUpTypes.TeamsInvite });
    }
  }, [inviteType]);

  if (isLoadingCustomSignUpExperience) {
    return (
      <WSFlexBox.Center py="XXXL">
        <WSLoader.Spinner />
      </WSFlexBox.Center>
    );
  }

  let title =
    partner?.header ?? enterprise?.signUpHeader ?? "Welcome to Wingspan";
  let description =
    "The best way to work for yourself! Wingspan provides independent professionals with invoicing, expense tracking, taxes, benefits - all back office needs, all in one place.";
  let featureItems = [
    "Supercharged invoicing - retainers, auto reminders & more",
    "Easy subcontractor payments",
    "Automated tax withholding and quarterly filings",
    "Expense tracking",
    "Big company benefits for you and yours"
  ];
  if (store.isEmployerSignUp) {
    title = "Welcome to Wingspan for Employers";
    description = "The best way to onboard, pay and manage your contractors.";
    featureItems = [
      "90-second contractor onboarding",
      "Fast payments",
      "Automatic 1099 filling",
      "Integrations & reporting",
      "Health & financial benefits for contractors"
    ];
  } else if (partner && (partner as IWebflowPartner)?.description) {
    description = (partner as IWebflowPartner).description ?? description;
  } else if (partner) {
    description =
      "Invoices, benefits, and bookkeeping for independent professionals, all in one place. Wingspan is the best way to work for yourself.";
  } else if (companyName) {
    description = `${companyName} wants to pay you through Wingspan. Create your free account now, setup your direct deposit information, and get paid!`;
  } else if (inviteType === "teams") {
    description = `To access ${inviterName}'s invite and log into ${
      inviterCompany ? `${inviterCompany}'s` : "their"
    } Wingspan account, simply register for a free Wingspan account. After registration, you'll be taken directly to their account.`;
  } else if (enterprise) {
    if (enterprise.signUpDescriptionItems) {
      featureItems = enterprise.signUpDescriptionItems.split(
        /\r\n|\n\r|\n|\r/g
      );
    }
    if (enterprise.signUpDescription) {
      description = enterprise.signUpDescription;
    }
  }

  return (
    <React.Fragment>
      <BrowserPageTitle title="Sign up" />
      <OnboardingLayout
        onBack={() => history.goBack()}
        partnerLogo={partner?.logo?.src || enterprise?.logo.src}
      >
        <WSGrid.Container>
          <WSScreen.TabletAndDesktop>
            <WSGrid.Item XS={6} M={6}>
              <SignUpWelcome
                name={enterprise?.name}
                title={title}
                description={description}
                featureItems={featureItems}
              />
            </WSGrid.Item>
          </WSScreen.TabletAndDesktop>
          <WSScreen.Mobile>
            <WSGrid.Item XS={6} M={6}>
              <SignUpWelcome
                showContent={false}
                name={enterprise?.name}
                title={title}
                description={description}
                featureItems={featureItems}
              />
            </WSGrid.Item>
          </WSScreen.Mobile>
          <WSGrid.Item XS={6} M={6}>
            <SignUpForm
              defaultValues={{
                email: email || inviteeEmail || refereeEmail || ""
              }}
              title={
                store.is1099SignUp
                  ? "First, create an account"
                  : enterprise?.signUpFormHeader
              }
              description={
                store.is1099SignUp
                  ? "You will need a Wingspan account to access your tax form when they're ready."
                  : ""
              }
              buttonText={
                inviteType === "teams"
                  ? "Create account"
                  : "Get started with Wingspan"
              }
            />
          </WSGrid.Item>
          {// render only 1099-sign up specific content here
          enterprise?.is1099FilingOnly ? (
            <WSScreen.Mobile>
              <WSGrid.Item XS={6} M={6}>
                <SignUpWelcome
                  showHeader={false}
                  name={enterprise?.name}
                  title={title}
                  description={description}
                  featureItems={featureItems}
                />
              </WSGrid.Item>
            </WSScreen.Mobile>
          ) : null}
        </WSGrid.Container>
      </OnboardingLayout>
    </React.Fragment>
  );
};
